// The wait, in our own mark (docs/brand/BRAND.md, "Visual concept"): the nine squares of the logo fill one by one
// in production order, writing to final, then let go and start again, the way a production fills its row of
// squares. In the nine department colours where the wait is the work (a take rendering, a screen opening); in the
// text colour inside a button or a line of text, where colour would claim a department. Reduced motion: the mark
// stands still and the words next to it carry the state. CSS: ui/motion.css.
import { type CSSProperties, useEffect, useState } from "react";
import { BRAND } from "../brand.ts";
import { BrandMark, MARK_SQUARES } from "./icons.tsx";

export function MarkLoader({
  size = 14,
  tone = "ink",
  label,
  className = "",
}: {
  size?: number;
  /** "dept": the nine department colours; "ink": the current text colour */
  tone?: "dept" | "ink";
  /** spoken label; without one the loader is decoration next to words that say it */
  label?: string;
  className?: string;
}) {
  // another brand's mark has no squares to fill: it breathes
  const svg = BRAND.mark ? (
    <BrandMark src={BRAND.mark} size={size} className="ml-breathe" />
  ) : (
    <svg width={size} height={size} viewBox="0 0 22 22" aria-hidden="true" focusable="false">
      {MARK_SQUARES.map((d, i) => (
        <rect
          key={d}
          x={(i % 3) * 8}
          y={Math.floor(i / 3) * 8}
          width="6"
          height="6"
          rx="1.5"
          fill={tone === "dept" ? `var(--dept-${d})` : "currentColor"}
          style={{ "--i": i } as CSSProperties}
        />
      ))}
    </svg>
  );
  const cls = `ml ml-${tone} ${className}`;
  return label ? (
    <span className={cls} role="status" aria-label={label}>
      {svg}
    </span>
  ) : (
    <span className={cls} aria-hidden="true">
      {svg}
    </span>
  );
}

/**
 * A screen that is still opening: the mark, centred. It shows only after a beat (CSS), so a fast load never
 * flashes it.
 */
export function Splash({ label = "Loading" }: { label?: string }) {
  return (
    <div className="splash" role="status" aria-busy="true">
      <MarkLoader size={28} tone="dept" />
      <span className="sr-only">{label}</span>
    </div>
  );
}

/** Seconds since `since` as a running clock, "0:07", "1:42", "12:03"; ticks once a second while mounted. */
export function useElapsed(since: string | null | undefined): string | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!since) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [since]);
  if (!since) return null;
  const at = Date.parse(since);
  if (!Number.isFinite(at)) return null;
  const s = Math.max(0, Math.floor((now - at) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
